import { Dynamic } from "solid-js/web";
import { cn } from "@folio/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Wordmark element. By default the large wordmark is an `h1` (it is the
   * page's title mark); pass `"p"` when the logo is DECORATIVE and the real
   * heading lives elsewhere (e.g. the auth brand panel already provides the
   * `h1`, so the panel logo must be `p` to avoid duplicating landmarks). Also
   * accepts `"span"`.
   */
  as?: "h1" | "p" | "span";
}

/**
 * Folio brand mark — premium editorial office suite.
 *
 * The glyph is a **folio sheet** with a folded corner and, inside it, the
 * venation of a botanical leaf (midrib + veins): the double meaning of "folio"
 * — paper sheet and leaf/foliage (folium). Sober, editorial and creative, with
 * no generic document icons.
 *
 * CONTRAST IN ALL THEMES (GymLog-style gotcha): the mark is NOT drawn with
 * `text-accent` over a faint surface (translucent `bg-surface` is nearly
 * invisible and `text-accent` disappears in several themes). Instead, the glyph
 * sits on a SOLID `bg-accent` chip and is painted with `text-accent-foreground`,
 * a token pair with contrast guaranteed by construction in every bundled theme.
 * The wordmark uses the brand editorial serif (Newsreader) in
 * `text-foreground`, which also contrasts with the background in light/dark mode.
 */
function FolioMark(props: { class?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={props.class}
      aria-hidden="true"
    >
      {/* Hoja de folio con esquina doblada */}
      <path
        d="M8 4h11l5 5v17a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
        fill="currentColor"
        fill-opacity="0.16"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linejoin="round"
      />
      <path d="M19 4v5h5" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" />
      {/* Nervadura de hoja (folium): midrib + venas */}
      <path d="M15 11.5V24" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
      <path
        d="M15 15.5 11.5 13M15 15.5 18.5 13M15 19.5 11.5 17M15 19.5 18.5 17"
        stroke="currentColor"
        stroke-width="1.4"
        stroke-linecap="round"
        stroke-opacity="0.72"
      />
    </svg>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground shadow-sm shadow-accent/25 ring-1 ring-black/5">
            <FolioMark class="h-6 w-6" />
          </div>
          <Dynamic
            component={props.as ?? "span"}
            class="font-serif text-xl font-semibold tracking-tight text-foreground"
          >
            {}
            Folio
            {}
          </Dynamic>
        </>
      ) : (
        <>
          <div class="mb-1 flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground shadow-md shadow-accent/30 ring-1 ring-black/5 transition-transform duration-300 hover:scale-105 active:scale-95">
            <FolioMark class="h-9 w-9" />
          </div>
          <Dynamic
            component={props.as ?? "h1"}
            class="font-serif text-3xl font-semibold tracking-tight text-foreground"
          >
            {}
            Folio
            {}
          </Dynamic>
        </>
      )}
    </div>
  );
}
